import { useState } from "react";
import { CheckCircle, Circle } from "lucide-react";
import Swal from "sweetalert2";
import { usePromotionalChat } from "../hooks/usePromotionalChat";
import { useAuth } from "../hooks/useAuth";

const EXAMPLE_MESSAGES = [
  "Hola, estoy interesado en sus productos. ¿Pueden enviarme su catálogo?",
  "Quisiera conocer más sobre las promociones actuales.",
  "¿Hacen envíos a todo el país? Estoy en busca de un proveedor.",
  "Estoy organizando un evento y me interesa su marca. ¿Podemos hablar?",
  "Hola, me gustaría ser revendedor. ¿Qué requisitos tienen?",
];

export default function FirstMessageToPromotionalModal({
  open,
  onClose,
  promoProfileId, // 🔒 UID del perfil promocional
  promoProfileName = "", // 🪪 Nombre visible del perfil
}) {
  const [selectedMessages, setSelectedMessages] = useState([]);
  const { uid: userId } = useAuth() || {}; // Obtener userId del hook de autenticación
  const { sendMessage, loading, error } = usePromotionalChat(userId, promoProfileId);

  if (!open) return null;

  const toggleMessage = (msg) => {
    setSelectedMessages((prev) =>
      prev.includes(msg) ? prev.filter((m) => m !== msg) : [...prev, msg]
    );
  };

  const handleSend = async () => {
    if (selectedMessages.length === 0) {
      Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Por favor selecciona al menos un mensaje.", showConfirmButton: false, timer: 3000 });
      return;
    }

    const finalMessage = selectedMessages.join(" ");

    try {
      await sendMessage(finalMessage);
      setSelectedMessages([]); // Limpiar selección
      onClose(); // Cerrar modal
    } catch (err) {
      console.error("Error al enviar mensaje:", err);
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Ocurrió un error al enviar el mensaje. Intenta de nuevo.", showConfirmButton: false, timer: 3500 });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 w-full max-w-lg relative">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
          Enviar primer mensaje a:
        </h2>
        <p className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-4">
          {promoProfileName}
        </p>

        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
          Para iniciar una conversación con esta cuenta promocional, debes
          enviar un primer mensaje desde aquí. Una vez enviado, la conversación
          aparecerá en tu chat normal con el resto de tus conversaciones.
        </p>

        {error && (
          <p className="text-sm text-red-500 mb-4">{error}</p>
        )}

        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 max-h-64 overflow-y-auto space-y-3 border border-gray-200 dark:border-gray-700">
          {EXAMPLE_MESSAGES.map((msg, index) => {
            const selected = selectedMessages.includes(msg);
            return (
              <div
                key={index}
                onClick={() => toggleMessage(msg)}
                className={`flex items-start space-x-2 cursor-pointer p-2 rounded-md transition ${
                  selected
                    ? "bg-blue-100 dark:bg-blue-700/40 border-l-4 border-blue-500"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                {selected ? (
                  <CheckCircle className="w-5 h-5 text-blue-500 mt-1" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-400 mt-1" />
                )}
                <p className="text-sm text-gray-700 dark:text-gray-200">{msg}</p>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end mt-6 space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 transition"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            onClick={handleSend}
            className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition disabled:bg-blue-400"
            disabled={loading}
          >
            {loading ? "Enviando..." : "Enviar mensaje"}
          </button>
        </div>
      </div>
    </div>
  );
}